import { useState } from 'react'
import { apiErrorText } from '@/lib/api-error'
import { inlineInput } from '@/lib/form-styles'
import { importLedgerAccounts } from '../api/incoming-invoices.api'
import type { IncomingSettings, LedgerImportResult, LedgerImportRow } from '../types/incoming-invoices.types'

/** `inlineInput` ohne `w-full` — sonst überstimmt es jede gesetzte Breite. */
const schmal = inlineInput.replace('w-full', '')
const knopf = 'rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50'

const GRUND: Record<NonNullable<LedgerImportRow['reason']>, string> = {
  NUMBER_MISSING: 'ohne Nummer',
  NAME_MISSING: 'ohne Bezeichnung',
  NUMBER_TOO_LONG: 'Nummer länger als 20 Zeichen',
  NAME_TOO_LONG: 'Bezeichnung länger als 120 Zeichen',
}

function ergebnis(z: LedgerImportRow, namenUebernehmen: boolean): { text: string; klasse: string } {
  switch (z.state) {
    case 'NEW':
      return { text: 'neu', klasse: 'text-green-700 dark:text-green-400' }
    case 'SAME':
      return { text: 'schon vorhanden', klasse: 'text-muted-foreground' }
    case 'RENAME':
      return { text: `vorhanden als „${z.existingName ?? ''}" — ${namenUebernehmen ? 'wird umbenannt' : 'bleibt so'}`, klasse: 'text-amber-700 dark:text-amber-400' }
    case 'DUPLICATE':
      return { text: 'doppelt in der Datei — die erste Zeile gilt', klasse: 'text-amber-700 dark:text-amber-400' }
    default:
      return { text: `nicht einlesbar: ${z.reason ? GRUND[z.reason] : 'unvollständig'}`, klasse: 'text-destructive' }
  }
}

/**
 * **Sachkonten importieren** (Auftraggeber 6.10.2026): der Kontenplan aus der Buchhaltung als CSV- oder
 * Excel-Datei, oder die Zeilen aus einer Tabelle kopiert und eingefügt. Gebraucht werden zwei Spalten —
 * Nummer und Bezeichnung; der Server erkennt sie und die Überschrift, die Maske lässt beides ändern.
 *
 * Erst die Vorschau mit dem Ergebnis je Zeile, dann der Import: dieselbe Anfrage, einmal mit `dryRun`.
 * Angelegt wird nur, was neu ist; vorhandene Konten bleiben, ihre Bezeichnung ändert sich nur auf Wunsch.
 */
export function SachkontenImportDialog({ onClose, onFertig }: { onClose: () => void; onFertig: (einst: IncomingSettings, meldung: string) => void }) {
  const [datei, setDatei] = useState<File | null>(null)
  const [text, setText] = useState('')
  const [vorschau, setVorschau] = useState<LedgerImportResult | null>(null)
  const [namenUebernehmen, setNamenUebernehmen] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  type Spalten = { hasHeader?: boolean; numberColumn?: number; nameColumn?: number }
  const quelle = (d: File | null = datei) => (d ? { datei: d } : { text })

  /** Vorschau rechnen — ohne `spalten` erkennt der Server Überschrift und Spalten selbst. */
  const pruefen = async (d: File | null = datei, spalten: Spalten = {}) => {
    if (!d && !text.trim()) return
    setLaeuft(true)
    setFehler(null)
    try {
      setVorschau(await importLedgerAccounts(quelle(d), { dryRun: true, ...spalten }))
    } catch (e) {
      setVorschau(null)
      setFehler(apiErrorText(e, 'Die Datei ließ sich nicht lesen.'))
    } finally {
      setLaeuft(false)
    }
  }

  /** Eine Spalte oder die Überschrift von Hand ändern — die übrige Wahl bleibt. */
  const mitSpalten = (teil: Spalten) => {
    if (!vorschau) return
    const c = vorschau.columns
    void pruefen(datei, {
      hasHeader: c.hasHeader,
      ...(c.number >= 0 && { numberColumn: c.number }),
      ...(c.name >= 0 && { nameColumn: c.name }),
      ...teil,
    })
  }

  const anzahl = vorschau ? vorschau.counts.new + (namenUebernehmen ? vorschau.counts.rename : 0) : 0
  const importieren = async () => {
    if (!vorschau || !anzahl) return
    setLaeuft(true)
    setFehler(null)
    try {
      const c = vorschau.columns
      const r = await importLedgerAccounts(quelle(), { hasHeader: c.hasHeader, numberColumn: c.number, nameColumn: c.name, updateNames: namenUebernehmen })
      if (!r.settings) throw new Error('ohne Ergebnis')
      const teile = [r.created ? `${r.created} ${r.created === 1 ? 'Sachkonto' : 'Sachkonten'} angelegt` : null, r.renamed ? `${r.renamed} umbenannt` : null].filter(Boolean)
      onFertig(r.settings, `${teile.join(', ') || 'Nichts geändert'}.`)
    } catch (e) {
      setFehler(apiErrorText(e, 'Der Import hat nicht geklappt.'))
      setLaeuft(false)
    }
  }

  const c = vorschau?.columns
  const spaltenUnklar = !!c && (c.number < 0 || c.name < 0 || c.number === c.name)
  /** Beschriftung einer Spalte in der Auswahl: die Überschrift, sonst „Spalte n" mit einem Beispiel. */
  const spaltenName = (i: number) => {
    if (!vorschau) return ''
    const kopf = c?.hasHeader ? vorschau.sample[0]?.[i]?.trim() : ''
    const beispiel = vorschau.sample[c?.hasHeader ? 1 : 0]?.[i]?.trim()
    return `${kopf || `Spalte ${i + 1}`}${beispiel ? ` — z. B. ${beispiel.slice(0, 30)}` : ''}`
  }
  const spaltenWahl = (label: string, wert: number, feld: 'numberColumn' | 'nameColumn') => (
    <label className="block text-xs text-muted-foreground">
      {label}
      <select className={`${schmal} mt-0.5 block w-64`} value={wert < 0 ? '' : wert} disabled={laeuft} onChange={(e) => e.target.value !== '' && mitSpalten({ [feld]: Number(e.target.value) })}>
        {wert < 0 && <option value="">— bitte wählen</option>}
        {Array.from({ length: vorschau?.columnCount ?? 0 }, (_, i) => (
          <option key={i} value={i}>
            {spaltenName(i)}
          </option>
        ))}
      </select>
    </label>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && !laeuft && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Sachkonten importieren"
        className="flex max-h-[90vh] w-full max-w-3xl flex-col gap-3 overflow-hidden rounded-lg border border-border p-5 shadow-xl"
        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
      >
        <div>
          <h2 className="text-lg font-semibold text-foreground">Sachkonten importieren</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Den Kontenplan aus der Buchhaltung als CSV- oder Excel-Datei (XLSX) wählen — oder die Zeilen in einer Tabelle kopieren und unten einfügen. Gebraucht werden zwei Spalten: Nummer und Bezeichnung. Angelegt wird nur, was neu ist; vorhandene
            Konten bleiben.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs text-muted-foreground">
            Datei (CSV oder XLSX)
            <input
              type="file"
              accept=".csv,.txt,.xlsx"
              className="mt-1 block w-full text-sm text-foreground"
              onChange={(e) => {
                const d = e.target.files?.[0] ?? null
                setDatei(d)
                setVorschau(null)
                if (d) void pruefen(d)
              }}
            />
          </label>
          <label className="block text-xs text-muted-foreground">
            … oder aus einer Tabelle Kopiertes einfügen
            <span className="mt-1 flex gap-2">
              <textarea
                rows={2}
                aria-label="Kopierte Zeilen"
                placeholder={'5000\tWareneinsatz\n5800\tFremdleistungen'}
                className={`${inlineInput} min-w-0 flex-1 font-mono text-xs`}
                value={text}
                disabled={!!datei}
                onChange={(e) => {
                  setText(e.target.value)
                  setVorschau(null)
                }}
              />
              <button type="button" className={`${knopf} self-start`} disabled={!!datei || !text.trim() || laeuft} onClick={() => void pruefen(null)}>
                Prüfen
              </button>
            </span>
          </label>
        </div>

        {vorschau && (
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="flex flex-wrap items-end gap-3">
              {spaltenWahl('Spalte der Nummer', c!.number, 'numberColumn')}
              {spaltenWahl('Spalte der Bezeichnung', c!.name, 'nameColumn')}
              <label className="flex items-center gap-2 pb-1.5 text-xs text-muted-foreground">
                <input type="checkbox" checked={c!.hasHeader} disabled={laeuft} onChange={(e) => mitSpalten({ hasHeader: e.target.checked })} /> erste Zeile ist die Überschrift
              </label>
            </div>

            {spaltenUnklar ? (
              <p className="rounded-md bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">Die Spalten ließen sich nicht sicher erkennen — bitte oben wählen, wo die Nummer und wo die Bezeichnung steht.</p>
            ) : (
              <>
                <p className="text-sm">
                  <strong className="text-green-700 dark:text-green-400">{vorschau.counts.new} neu</strong>
                  {' · '}
                  {vorschau.counts.same} schon vorhanden
                  {vorschau.counts.rename > 0 && ` · ${vorschau.counts.rename} mit anderer Bezeichnung`}
                  {vorschau.counts.duplicate > 0 && ` · ${vorschau.counts.duplicate} doppelt`}
                  {vorschau.counts.invalid > 0 && ` · ${vorschau.counts.invalid} nicht einlesbar`}
                </p>
                {vorschau.counts.rename > 0 && (
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={namenUebernehmen} onChange={(e) => setNamenUebernehmen(e.target.checked)} />
                    {vorschau.counts.rename === 1 ? 'Bezeichnung des vorhandenen Kontos' : `Bezeichnung der ${vorschau.counts.rename} vorhandenen Konten`} aus der Datei übernehmen
                  </label>
                )}
                <div className="min-h-0 flex-1 overflow-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 text-left text-muted-foreground" style={{ background: 'hsl(var(--card))' }}>
                      <tr>
                        <th className="w-12 px-2 py-1 font-normal">Zeile</th>
                        <th className="w-28 px-2 py-1 font-normal">Nummer</th>
                        <th className="px-2 py-1 font-normal">Bezeichnung</th>
                        <th className="px-2 py-1 font-normal">Ergebnis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vorschau.rows.map((z) => {
                        const e = ergebnis(z, namenUebernehmen)
                        return (
                          <tr key={z.row} className="border-t border-border">
                            <td className="px-2 py-0.5 tabular-nums text-muted-foreground">{z.row}</td>
                            <td className="px-2 py-0.5 tabular-nums">{z.number || '—'}</td>
                            <td className="px-2 py-0.5">{z.name || '—'}</td>
                            <td className={`px-2 py-0.5 ${e.klasse}`}>{e.text}</td>
                          </tr>
                        )
                      })}
                      {vorschau.rows.length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-2 py-3 text-center text-muted-foreground">
                            Keine Zeile mit Nummer und Bezeichnung gefunden.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}

        <div className="flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-muted" onClick={onClose} disabled={laeuft}>
            Abbrechen
          </button>
          <button type="button" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50" disabled={!vorschau || spaltenUnklar || anzahl === 0 || laeuft} onClick={() => void importieren()}>
            {anzahl > 0 ? `${anzahl} ${anzahl === 1 ? 'Sachkonto' : 'Sachkonten'} importieren` : 'Importieren'}
          </button>
        </div>
      </div>
    </div>
  )
}
